import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ---------- helpers ----------
const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '-' : dt.toLocaleDateString();
};
const fmtMoney = (n) => `Rs. ${Math.round(Number(n) || 0).toLocaleString()}`;
const sourceOf = (item) => item.supplier_name || item.buyback_customer_name || 'Initial Stock';

const STATUS_THEMES = {
    available: { bg: '#dcfce7', fg: '#166534', dot: '#22c55e' },
    sold: { bg: '#dbeafe', fg: '#1e40af', dot: '#3b82f6' },
    reserved: { bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' },
    hold: { bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' },
};
const DEFAULT_THEME = { bg: '#e2e8f0', fg: '#334155', dot: '#64748b' };
const getStatusTheme = (s) => STATUS_THEMES[String(s || '').toLowerCase()] || DEFAULT_THEME;

const StatusBadge = ({ status, large }) => {
    const t = getStatusTheme(status);
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: t.bg, color: t.fg, borderRadius: '999px',
            padding: large ? '8px 18px' : '4px 12px',
            fontSize: large ? '13px' : '11px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase'
        }}>
            <span style={{ width: large ? 10 : 8, height: large ? 10 : 8, borderRadius: '50%', background: t.dot }} />
            {status || 'Unknown'}
        </span>
    );
};

const Empty = ({ icon, text }) => (
    <div style={styles.empty}>
        <div style={{ fontSize: '40px', marginBottom: '8px' }}>{icon}</div>
        <div>{text}</div>
    </div>
);

const InfoRow = ({ label, value }) => (
    <div style={styles.dataRow}>
        <span style={{ color: '#64748b' }}>{label}</span>
        <b style={{ color: '#0f172a', textAlign: 'right' }}>{value === null || value === undefined || value === '' ? '-' : value}</b>
    </div>
);

const BikeLifeCycleView = ({ bikeId, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('summary');

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const cleanId = String(bikeId).split(':')[0];
            const res = await API.get(`/v1/bikes/lifecycle/${cleanId}`);
            if (res.data.success) setData(res.data.data);
            else setError(res.data.message || 'Bike data could not be loaded.');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.response?.data?.error || 'Bike data could not be loaded.');
        } finally {
            setLoading(false);
        }
    }, [bikeId]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const generateProfessionalPDF = () => {
        const { bike_info, inventory, sales, registration_history } = data;
        const plate = registration_history.find(r => r.is_current)?.reg_number || 'APPLIED';
        const doc = new jsPDF();

        // --- Header Section ---
        doc.setFillColor(15, 23, 42);
        doc.rect(0, 0, 210, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24).setFont("helvetica", "bold").text('UNITED MOTORS', 105, 20, { align: 'center' });
        doc.setFontSize(10).setFont("helvetica", "normal").text('COMPLETE VEHICLE LIFECYCLE & OWNERSHIP AUDIT', 105, 28, { align: 'center' });
        doc.setFontSize(8).text(`Report Generated: ${new Date().toLocaleString()}`, 105, 36, { align: 'center' });

        // --- 1. Vehicle Identity ---
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(14).setFont("helvetica", "bold").text('1. VEHICLE SPECIFICATIONS', 14, 58);
        doc.setDrawColor(226, 232, 240).line(14, 60, 196, 60);

        autoTable(doc, {
            startY: 63,
            theme: 'plain',
            styles: { fontSize: 9, cellPadding: 2 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 35 }, 2: { fontStyle: 'bold', cellWidth: 35 } },
            body: [
                ['Make:', bike_info.model_name || '-', 'Version:', bike_info.version_type || '-'],
                ['CC:', bike_info.cc ?? '-', 'Color:', bike_info.color || '-'],
                ['Engine No:', bike_info.engine_number || '-', 'Chassis No:', bike_info.chassis_number || '-'],
                ['Current Status:', bike_info.stock_status || 'N/A', 'Registration:', plate]
            ]
        });

        // --- 2. Sales & Ownership History (Blue) ---
        doc.setFontSize(12).setFont("helvetica", "bold").text('2. OWNERSHIP & SALES HISTORY', 14, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 13,
            theme: 'grid',
            headStyles: { fillColor: [37, 99, 235] },
            head: [['Owner #', 'Date', 'Customer Name', 'Sale Type', 'Net Amount']],
            body: sales.map((s, i) => [
                `Owner ${sales.length - i}`,
                fmtDate(s.sale_date),
                (s.full_name || '').toUpperCase(),
                s.sale_type || '-',
                fmtMoney(s.net_amount)
            ]),
        });

        // --- 3. Inventory & Purchase Log (Green) ---
        doc.setFontSize(12).setFont("helvetica", "bold").text('3. STOCK & ACQUISITION HISTORY', 14, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 13,
            theme: 'grid',
            headStyles: { fillColor: [16, 185, 129] },
            head: [['Entry Date', 'Type', 'Source / Supplier', 'Status', 'Purchase Price']],
            body: inventory.map(item => [
                fmtDate(item.purchase_date),
                item.purchase_type || '-',
                sourceOf(item).toUpperCase(),
                item.stock_status || '-',
                fmtMoney(item.purchase_price || item.purchase_cost)
            ]),
        });

        // --- 4. Registration History (Slate) ---
        doc.setFontSize(12).setFont("helvetica", "bold").text('4. REGISTRATION & PLATE LOG', 14, doc.lastAutoTable.finalY + 10);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 13,
            theme: 'grid',
            headStyles: { fillColor: [71, 85, 105] },
            head: [['Plate Number', 'Assigned Date', 'Released Date', 'Current Status']],
            body: registration_history.map(r => [
                r.reg_number,
                fmtDate(r.assigned_date),
                r.released_date ? fmtDate(r.released_date) : 'Active',
                r.is_current ? 'CURRENT' : 'PREVIOUS'
            ]),
        });

        // Final Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8).setTextColor(150).text(`United Motors - Page ${i} of ${pageCount} | Engine: ${bike_info.engine_number}`, 105, 290, { align: 'center' });
        }

        doc.save(`Complete_History_${bike_info.engine_number}.pdf`);
    };

    // ---------- loading / error states ----------
    if (loading) {
        return (
            <div style={styles.overlay}>
                <div style={styles.loader}>
                    <div className="blc-spinner" />
                    <div style={{ marginTop: '16px', fontWeight: 600 }}>Loading bike profile...</div>
                </div>
                <style>{css}</style>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div style={styles.overlay} onClick={onClose}>
                <div style={styles.errorBox} onClick={(e) => e.stopPropagation()}>
                    <div style={{ fontSize: '42px' }}>⚠️</div>
                    <h3 style={{ margin: '10px 0 6px', color: '#0f172a' }}>Profile load nahi ho saka</h3>
                    <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px' }}>{error || 'Koi data nahi mila.'}</p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button onClick={fetchHistory} style={styles.primaryBtn}>Retry</button>
                        <button onClick={onClose} style={styles.ghostBtn}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    const { bike_info, inventory = [], sales = [], registration_history = [] } = data;
    const currentPlate = registration_history.find(r => r.is_current)?.reg_number || 'APPLIED';
    const latestStock = inventory[0] || null;
    const lastSale = sales[0] || null;

    const tabs = [
        { id: 'summary', label: 'Summary', icon: '📋', color: '#475569' },
        { id: 'sales', label: 'Sales', icon: '🧾', color: '#2563eb', count: sales.length },
        { id: 'inventory', label: 'Stock History', icon: '📦', color: '#10b981', count: inventory.length },
        { id: 'registration', label: 'Registration', icon: '🔖', color: '#7c3aed', count: registration_history.length },
    ];

    return (
        <div style={styles.overlay} onClick={onClose}>
            <style>{css}</style>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

                {/* ===== HERO HEADER ===== */}
                <div style={styles.hero}>
                    <div style={styles.heroTop}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={styles.logoBadge}>UM</div>
                            <div>
                                <div style={styles.brand}>UNITED MOTORS</div>
                                <div style={styles.tagline}>INTELLIGENT HISTORY TRACKER</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <button onClick={generateProfessionalPDF} style={styles.downloadBtn}>📥 Export History</button>
                            <button onClick={onClose} style={styles.exitBtn} title="Close (Esc)">&times;</button>
                        </div>
                    </div>

                    <div style={styles.heroMain}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <h1 style={styles.bikeTitle}>{bike_info.model_name || 'Unknown Bike'}</h1>
                                <StatusBadge status={bike_info.stock_status} large />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                                {bike_info.version_type && <span style={styles.heroPill}>{bike_info.version_type}</span>}
                                {bike_info.cc ? <span style={styles.heroPill}>{bike_info.cc} CC</span> : null}
                                {bike_info.color && <span style={styles.heroPill}>🎨 {bike_info.color}</span>}
                            </div>
                        </div>
                        <div style={styles.plate}>
                            <div style={styles.plateLabel}>REGISTRATION</div>
                            <div style={styles.plateNumber}>{currentPlate}</div>
                        </div>
                    </div>
                </div>

                {/* ===== STAT CARDS ===== */}
                <div style={styles.statRow}>
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>Engine No</span>
                        <span style={styles.statValue}>{bike_info.engine_number || '-'}</span>
                    </div>
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>Chassis No</span>
                        <span style={styles.statValue}>{bike_info.chassis_number || '-'}</span>
                    </div>
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>Total Owners</span>
                        <span style={styles.statValue}>{sales.length}</span>
                    </div>
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>Purchase Cost</span>
                        <span style={styles.statValue}>{latestStock ? fmtMoney(latestStock.purchase_price || latestStock.purchase_cost) : '-'}</span>
                    </div>
                </div>

                {/* ===== TABS ===== */}
                <div style={styles.tabNav}>
                    {tabs.map(t => {
                        const active = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                style={{
                                    ...styles.tab,
                                    background: active ? t.color : 'transparent',
                                    color: active ? '#fff' : '#64748b',
                                }}
                            >
                                <span>{t.icon}</span>
                                <span>{t.label}</span>
                                {t.count !== undefined && (
                                    <span style={{
                                        ...styles.tabCount,
                                        background: active ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                                        color: active ? '#fff' : '#475569'
                                    }}>{t.count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* ===== CONTENT ===== */}
                <div style={styles.scrollContent}>
                    {activeTab === 'summary' && (
                        <div style={styles.grid}>
                            <div style={{ ...styles.card, borderTop: '4px solid #475569' }}>
                                <h4 style={styles.cardTitle}>⚙️ Technical Identity</h4>
                                <InfoRow label="Make" value={bike_info.model_name} />
                                <InfoRow label="Version" value={bike_info.version_type} />
                                <InfoRow label="CC" value={bike_info.cc} />
                                <InfoRow label="Color" value={bike_info.color} />
                                <InfoRow label="Engine" value={bike_info.engine_number} />
                                <InfoRow label="Chassis" value={bike_info.chassis_number} />
                            </div>

                            <div style={{ ...styles.card, borderTop: '4px solid #2563eb' }}>
                                <h4 style={styles.cardTitle}>👤 Ownership Summary</h4>
                                <div style={styles.dataRow}>
                                    <span style={{ color: '#64748b' }}>Current Status</span>
                                    <StatusBadge status={bike_info.stock_status} />
                                </div>
                                <InfoRow label="Total Owners" value={sales.length} />
                                <InfoRow label="Last Sale" value={lastSale ? fmtDate(lastSale.sale_date) : 'N/A'} />
                                <InfoRow label="Last Buyer" value={lastSale ? lastSale.full_name : 'N/A'} />
                                <InfoRow label="Last Sale Amount" value={lastSale ? fmtMoney(lastSale.net_amount) : 'N/A'} />
                            </div>

                            <div style={{ ...styles.card, borderTop: '4px solid #10b981' }}>
                                <h4 style={styles.cardTitle}>📦 Acquisition</h4>
                                <InfoRow label="Entry Type" value={latestStock ? latestStock.purchase_type : 'N/A'} />
                                <InfoRow label="Source" value={latestStock ? sourceOf(latestStock) : (bike_info.initial_source_supplier || 'N/A')} />
                                <InfoRow label="Purchase Date" value={latestStock ? fmtDate(latestStock.purchase_date) : 'N/A'} />
                                <InfoRow label="Purchase Price" value={latestStock ? fmtMoney(latestStock.purchase_price || latestStock.purchase_cost) : 'N/A'} />
                                {latestStock && latestStock.condition_note ? <InfoRow label="Condition" value={latestStock.condition_note} /> : null}
                            </div>

                            <div style={{ ...styles.card, borderTop: '4px solid #7c3aed' }}>
                                <h4 style={styles.cardTitle}>🔖 Registration</h4>
                                <InfoRow label="Current Plate" value={currentPlate} />
                                <InfoRow label="Plates Used" value={registration_history.length} />
                                <InfoRow
                                    label="Last Assigned"
                                    value={registration_history[0] ? fmtDate(registration_history[0].assigned_date) : 'N/A'}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'sales' && (
                        sales.length === 0 ? <Empty icon="🧾" text="Is bike ki abhi tak koi sale nahi hui." /> : (
                            <div style={styles.tableBox}>
                                <table className="blc-table" style={styles.table}>
                                    <thead style={{ background: '#2563eb', color: '#fff' }}>
                                        <tr><th>DATE</th><th>CUSTOMER</th><th>TYPE</th><th style={{ textAlign: 'right' }}>AMOUNT</th></tr>
                                    </thead>
                                    <tbody>
                                        {sales.map((s, i) => (
                                            <tr key={i} style={styles.tr}>
                                                <td>{fmtDate(s.sale_date)}</td>
                                                <td><b>{s.full_name}</b></td>
                                                <td><span style={styles.badgeBlue}>{s.sale_type || '-'}</span></td>
                                                <td style={{ textAlign: 'right' }}><b>{fmtMoney(s.net_amount)}</b></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    {activeTab === 'inventory' && (
                        inventory.length === 0 ? <Empty icon="📦" text="Stock ki koi history nahi mili." /> : (
                            <div style={styles.tableBox}>
                                <table className="blc-table" style={styles.table}>
                                    <thead style={{ background: '#10b981', color: '#fff' }}>
                                        <tr><th>DATE</th><th>ENTRY</th><th>SOURCE</th><th>STATUS</th><th style={{ textAlign: 'right' }}>PURCHASE PRICE</th></tr>
                                    </thead>
                                    <tbody>
                                        {inventory.map((item, i) => (
                                            <tr key={i} style={styles.tr}>
                                                <td>{fmtDate(item.purchase_date)}</td>
                                                <td><span style={styles.badgeGreen}>{item.purchase_type || '-'}</span></td>
                                                <td>{sourceOf(item)}</td>
                                                <td><StatusBadge status={item.stock_status} /></td>
                                                <td style={{ textAlign: 'right' }}><b>{fmtMoney(item.purchase_price || item.purchase_cost)}</b></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    {activeTab === 'registration' && (
                        registration_history.length === 0 ? <Empty icon="🔖" text="Abhi tak koi registration number assign nahi hua." /> : (
                            <div style={styles.tableBox}>
                                <table className="blc-table" style={styles.table}>
                                    <thead style={{ background: '#7c3aed', color: '#fff' }}>
                                        <tr><th>PLATE NO</th><th>ASSIGNED</th><th>RELEASED</th><th>STATUS</th></tr>
                                    </thead>
                                    <tbody>
                                        {registration_history.map((r, i) => (
                                            <tr key={i} style={styles.tr}>
                                                <td style={{ color: '#2563eb', fontWeight: 'bold' }}>{r.reg_number}</td>
                                                <td>{fmtDate(r.assigned_date)}</td>
                                                <td>{r.released_date ? fmtDate(r.released_date) : '-'}</td>
                                                <td>
                                                    <span style={r.is_current ? styles.badgeGreen : styles.badgeGray}>
                                                        {r.is_current ? 'Current' : 'History'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

// Scoped CSS (only affects this dialog, not the rest of the app)
const css = `
.blc-table th, .blc-table td { padding: 14px 18px; text-align: left; }
.blc-table th { font-size: 11px; letter-spacing: 0.6px; }
.blc-table tbody tr:hover { background: #f8fafc; }
.blc-spinner { width: 44px; height: 44px; border: 4px solid rgba(255,255,255,0.25); border-top-color: #fff; border-radius: 50%; margin: 0 auto; animation: blc-spin 0.8s linear infinite; }
@keyframes blc-spin { to { transform: rotate(360deg); } }
@keyframes blc-pop { from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: none; } }
`;

const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(8px)' },
    modal: { background: '#f8fafc', width: '95%', maxWidth: '1100px', height: '90vh', borderRadius: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.45)', animation: 'blc-pop 0.25s ease-out' },

    hero: { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)', padding: '22px 36px 26px', color: '#fff' },
    heroTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' },
    logoBadge: { background: 'rgba(255,255,255,0.12)', color: '#fff', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.2)' },
    brand: { fontSize: '16px', fontWeight: 800, letterSpacing: '1px' },
    tagline: { fontSize: '10px', color: '#94a3b8', fontWeight: 'bold', letterSpacing: '1px' },
    heroMain: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap' },
    bikeTitle: { margin: 0, fontSize: '32px', fontWeight: 800, letterSpacing: '-0.5px' },
    heroPill: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '999px', padding: '5px 14px', fontSize: '12px', fontWeight: 600 },
    plate: { background: '#fff', color: '#0f172a', borderRadius: '10px', padding: '8px 22px 10px', textAlign: 'center', border: '3px solid #0f172a', boxShadow: '0 6px 18px rgba(0,0,0,0.35)', minWidth: '160px' },
    plateLabel: { fontSize: '9px', fontWeight: 800, color: '#64748b', letterSpacing: '1.5px' },
    plateNumber: { fontSize: '24px', fontWeight: 900, letterSpacing: '2px', textTransform: 'uppercase' },

    downloadBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    exitBtn: { fontSize: '26px', lineHeight: 1, background: 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer', color: '#fff', width: '38px', height: '38px', borderRadius: '10px' },

    statRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', padding: '18px 36px 6px' },
    statCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    statLabel: { fontSize: '10px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.8px', textTransform: 'uppercase' },
    statValue: { fontSize: '15px', fontWeight: 800, color: '#0f172a', wordBreak: 'break-all' },

    tabNav: { display: 'flex', gap: '8px', padding: '14px 36px 0', flexWrap: 'wrap' },
    tab: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 16px', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '13px', transition: '0.2s' },
    tabCount: { borderRadius: '999px', padding: '1px 8px', fontSize: '11px', fontWeight: 800 },

    scrollContent: { flex: 1, padding: '20px 36px 30px', overflowY: 'auto' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' },
    card: { padding: '20px 22px', background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    cardTitle: { margin: '0 0 8px', fontSize: '14px', color: '#0f172a' },
    dataRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9', fontSize: '14px' },

    tableBox: { borderRadius: '14px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff' },
    table: { width: '100%', borderCollapse: 'collapse' },
    tr: { borderBottom: '1px solid #f1f5f9', fontSize: '14px' },
    badgeBlue: { background: '#eff6ff', color: '#2563eb', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' },
    badgeGreen: { background: '#ecfdf5', color: '#059669', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' },
    badgeGray: { background: '#f1f5f9', color: '#64748b', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' },

    empty: { textAlign: 'center', color: '#94a3b8', padding: '60px 20px', background: '#fff', borderRadius: '14px', border: '1px dashed #cbd5e1', fontWeight: 600 },

    errorBox: { background: '#fff', borderRadius: '18px', padding: '32px 40px', textAlign: 'center', maxWidth: '420px', boxShadow: '0 25px 60px rgba(0,0,0,0.4)' },
    primaryBtn: { background: '#1e293b', color: '#fff', border: 'none', padding: '10px 22px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' },
    ghostBtn: { background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px 22px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' },

    loader: { textAlign: 'center', color: '#fff' }
};

export default BikeLifeCycleView;
